import { lazy, Suspense, useState } from "react";
import { useAsync } from "./useAsync.js";
import {
  detachNeckDiagram,
  lessonNeckDiagrams,
  readNeckDiagram,
  type SavedNeckDiagram,
} from "./neckDiagrams/api.js";
import { NeckDiagramSheets } from "./neckDiagrams/NeckDiagramView.js";
const Builder = lazy(() =>
  import("./InlineNeckDiagramBuilder.js").then((m) => ({
    default: m.InlineNeckDiagramBuilder,
  })),
);

export function LessonNeckDiagramPanel({ lessonId }: { lessonId: string }) {
  const result = useAsync(() => lessonNeckDiagrams(lessonId), [lessonId]);
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<SavedNeckDiagram | null>(null);
  async function edit(id: string) {
    setBusy(true);
    setProblem(null);
    try {
      const saved = await readNeckDiagram(id);
      if (!saved.canEdit)
        throw new Error("Only the author or an owner can edit the original.");
      setEditing(saved.diagram);
    } catch (error) {
      setProblem(
        error instanceof Error
          ? error.message
          : "Could not open the saved original.",
      );
    } finally {
      setBusy(false);
    }
  }
  async function remove(id: string) {
    setBusy(true);
    setProblem(null);
    try {
      await detachNeckDiagram(lessonId, id);
      result.reload();
    } catch (error) {
      setProblem(
        error instanceof Error ? error.message : "Could not remove this sheet.",
      );
    } finally {
      setBusy(false);
    }
  }
  // Existing attachments remain available on every instrument. Only omit the
  // empty section; editor eligibility must never hide a saved lesson snapshot.
  const diagrams = result.data?.diagrams ?? [];
  if (!diagrams.length && !result.error && !problem && !editing) return null;
  return (
    <section aria-label="Neck diagrams from this lesson">
      {result.loading && <p>Loading neck diagrams…</p>}
      {(result.error || problem) && (
        <p className="error" role="alert">
          {result.error || problem}
        </p>
      )}
      {diagrams.map((sheet) => (
        <div key={sheet.id}>
          <NeckDiagramSheets sheets={[sheet]} />
          {result.data?.canManage === true && <div className="row">
            <button
              type="button"
              disabled={busy}
              onClick={() => void edit(sheet.id)}
            >
              Edit saved original
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => void remove(sheet.id)}
            >
              Remove from lesson
            </button>
          </div>}
        </div>
      ))}
      {editing && result.data?.canManage === true && (
        <Suspense fallback={<p>Opening diagram editor…</p>}>
          <Builder
            initial={editing}
            lessonId={lessonId}
            ensureLesson={() => Promise.resolve(lessonId)}
            onAdded={() => result.reload()}
            onClose={() => setEditing(null)}
          />
        </Suspense>
      )}
    </section>
  );
}
